import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useId,
  useCallback,
} from "react";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import SelectInput from "../../components/select/SelectInput";
import { GlobalContext } from "../../context/GlobalContext";
import ContentFetchTable from "../contents/ContentFetchTable";
import PaginationModule from "../../pagination/PaginationModule"; // adjust path if needed
import parseIds from "../../components/utils/ParseIDs"; // adjust path to where you saved parseIds.js
import UpdateTypeAction from "./UpdateTypeAction"; // adjust path if needed

/* ------------------------------------------------------------------ */
/* Config: change these if your backend differs                        */
/* ------------------------------------------------------------------ */
const ID_KEY = "id"; // field on each row that holds the reference ID
const FILTER_REFID_KEY = "refid"
const TYPE_KEY = "type"; // field on each row that holds the type
const TRANSFER_ENDPOINT = "/TransferData"; // POST { domain, records }

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 38, height: 38 }),
  valueContainer: (base) => ({ ...base, height: 38, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 38 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */
const DataTransfer = () => {
  // useId returns values like ":r1:" – strip colons so it is safe in selectors
  const uid = useId().replace(/:/g, "");
  const sourceId = `source-${uid}`;
  const selectId = `select-${uid}`;

  const { API_URL } = useContext(GlobalContext);

  // Filters
  const [domainOptions, setDomainOptions] = useState([]);
  const [urlOptions, setUrlOptions] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [selectedURL, setSelectedURL] = useState("");
  const [idsText, setIdsText] = useState("");
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);
  const [isLoadingUrls, setIsLoadingUrls] = useState(false);

  // Data
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [typeUpdating, setTypeUpdating] = useState(false);

  // Selection + paging
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10);

  const urlRequestRef = useRef(0); // ignores stale URL responses
  const busy = loading || transferring || typeUpdating;

  /* ---------------- derived values ---------------- */
  const currentPosts = useMemo(() => {
    const last = currentPage * postsPerPage;
    return filterData.slice(last - postsPerPage, last);
  }, [filterData, currentPage, postsPerPage]);

  const howManyPages = Math.max(1, Math.ceil(filterData.length / postsPerPage));

  // IDs keep their original type (number/string) so the table's own
  // `selectedIds.includes(row.id)` check keeps working
  const allRowIds = useMemo(
    () => filterData.map((row) => row[ID_KEY]),
    [filterData],
  );
  const selectedSet = useMemo(
    () => new Set(selectedIds.map(String)),
    [selectedIds],
  );
  const selectAll =
    allRowIds.length > 0 && allRowIds.every((id) => selectedSet.has(String(id)));

  // Keep the page number valid when rows are removed
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filterData.length / postsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [filterData.length, postsPerPage, currentPage]);

  /* ---------------- helpers ---------------- */
  const clearTable = useCallback(() => {
    setData([]);
    setFilterData([]);
    setSelectedIds([]);
    setCurrentPage(1);
  }, []);

  /* ---------------- load domains / URLs ---------------- */
  const loadDomains = useCallback(async () => {
    setIsLoadingDomains(true);
    try {
      const response = await axios.get(`${API_URL}/filterDomain`);
      setDomainOptions(
        response.data.map((x) => ({ label: x.domain, value: x.domain })),
      );
    } catch (error) {
      console.error(error);
      setDomainOptions([]);
      toast.error("Could not load domains. Refresh the page to try again.", {
        toastId: "domains-error",
      });
    } finally {
      setIsLoadingDomains(false);
    }
  }, [API_URL]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const loadURLs = async (domain) => {
    const requestId = ++urlRequestRef.current;
    setUrlOptions([]);
    setSelectedURL("");
    if (!domain) return;

    setIsLoadingUrls(true);
    try {
      const response = await axios.get(`${API_URL}/filterCurrentURL/${encodeURIComponent(domain)}`,
      );
      if (requestId !== urlRequestRef.current) return; // a newer request won
      const urlList = response.data.map((x) => ({
        label: x.cur_url,
        value: x.cur_url,
      }));
      setUrlOptions([{ label: "All", value: "all" }, ...urlList]);
    } catch (error) {
      if (requestId !== urlRequestRef.current) return;
      console.error(error);
      setUrlOptions([]);
      toast.error("Could not load URLs for this domain.", {
        toastId: "urls-error",
      });
    } finally {
      if (requestId === urlRequestRef.current) setIsLoadingUrls(false);
    }
  };

  const handleDomainChange = (option) => {
    const domain = option ? option.value : "";
    setSelectedDomain(domain);
    clearTable();
    setHasSearched(false);
    loadURLs(domain);
  };

  const handleURLChange = (option) => {
    setSelectedURL(option ? option.value : "");
    clearTable();
    setHasSearched(false);
  };

  /* ---------------- search ---------------- */
  const handleSearch = async () => {
    if (!selectedDomain) {
      toast.warn("Select a domain first.", { toastId: "need-domain" });
      return;
    }
    // if (!selectedURL) {
    //   toast.warn("Select a current URL (or All).", { toastId: "need-url" });
    //   return;
    // }

    const parsed = parseIds(idsText);
    if (parsed.error) {
      toast.error(parsed.error, { toastId: "bad-ids" });
      return;
    }

    setLoading(true);
    setSelectedIds([]);
    const toastId = toast.loading("Fetching records…");

    try {
      const response = await axios.post(`${API_URL}/filterContents`, {
        domain: selectedDomain,
        cur_url: selectedURL,
      });

      const rows = Array.isArray(response.data) ? response.data : [];
      const filtered = parsed.ids
        ? rows.filter((row) => parsed.ids.has(String(row[FILTER_REFID_KEY])))
        : rows;

      setData(rows);
      setFilterData(filtered);
      setCurrentPage(1);
      setHasSearched(true);

      toast.update(toastId, {
        render:
          filtered.length > 0
            ? `${filtered.length} record${filtered.length === 1 ? "" : "s"} found.`
            : "No records match these filters.",
        type: filtered.length > 0 ? "success" : "info",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
        draggable: true,
        closeButton: true,
      });
    } catch (e) {
      console.error(e);
      clearTable();
      setHasSearched(true);
      toast.update(toastId, {
        render: "Search failed. Check your connection and try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
        closeButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- selection ---------------- */
  const resolveRowId = (arg1, arg2) => {
    const candidates = [
      arg2,
      arg1 && arg1[ID_KEY] !== undefined ? arg1[ID_KEY] : undefined,
      arg1 && arg1.target ? arg1.target.dataset && arg1.target.dataset.id : arg1,
      arg1 && arg1.target ? arg1.target.value : undefined,
      arg1 && arg1.target ? arg1.target.id : undefined,
    ];
    for (const c of candidates) {
      if (c === undefined || c === null || typeof c === "object") continue;
      const row = data.find((r) => String(r[ID_KEY]) === String(c));
      if (row) return row[ID_KEY]; // keep the row's original type
    }
    return undefined;
  };

  const handleCheckboxChange = (arg1, arg2) => {
    const id = resolveRowId(arg1, arg2);
    if (id === undefined) {
      console.warn("Checkbox change ignored: row id not found", arg1, arg2);
      return;
    }
    setSelectedIds((prev) =>
      prev.some((x) => String(x) === String(id))
        ? prev.filter((x) => String(x) !== String(id))
        : [...prev, id],
    );
  };

  // Uses the checkbox's own checked value when given, so the header
  // checkbox and the state can never end up opposite to each other
  const handleSelectAll = (arg) => {
    let checked;
    if (typeof arg === "boolean") checked = arg;
    else if (arg && arg.target && typeof arg.target.checked === "boolean")
      checked = arg.target.checked;
    else checked = !selectAll;

    setSelectedIds(checked ? allRowIds : []);
  };

  const checkAll = handleSelectAll;

  // After the server confirms, show the new type in the table straight away
  const handleTypeUpdated = (ids, newType) => {
    const changed = new Set(ids.map(String));
    const apply = (row) =>
      changed.has(String(row[ID_KEY])) ? { ...row, [TYPE_KEY]: newType } : row;
    setData((prev) => prev.map(apply));
    setFilterData((prev) => prev.map(apply));
  };

  /* ---------------- transfer ---------------- */
  const Transfer = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Tick at least one row to transfer.", {
        toastId: "nothing-selected",
      });
      return;
    }

    // Send the full selected rows, not just their IDs
    const selectedData = data.filter((item) =>
      selectedSet.has(String(item[ID_KEY])),
    );
    const count = selectedData.length;

    if (count === 0) {
      setSelectedIds([]);
      toast.error("Selected rows were not found. Please select them again.", {
        toastId: "selection-lost",
      });
      return;
    }

    // Browser confirmation: Cancel stops here and nothing is sent
    const confirmed = window.confirm(
      `Transfer ${count} record${count === 1 ? "" : "s"} from "${selectedDomain}"?`,
    );
    if (!confirmed) {
      toast.info("Transfer cancelled. No data was changed.", {
        toastId: "transfer-cancelled",
      });
      return;
    }

    setTransferring(true);
    const toastId = toast.loading("Data processing, please wait...");

    try {
      const response = await axios.post(`${API_URL}${TRANSFER_ENDPOINT}`, {
        domain: selectedDomain,
        records: selectedData,
      });

      const result = response.data || {};
      if (result.success === false) {
        throw new Error(result.message || "Transfer failed.");
      }

      // Remove transferred rows from the table
      const done = new Set(selectedIds.map(String));
      const keep = (row) => !done.has(String(row[ID_KEY]));
      setData((prev) => prev.filter(keep));
      setFilterData((prev) => prev.filter(keep));
      setSelectedIds([]);

      // Show the server's own message
      toast.update(toastId, {
        render: result.message || "Transfer completed successfully.",
        type: "success",
        isLoading: false,
        autoClose: 7000,
        closeOnClick: true,
        draggable: true,
        closeButton: true,
      });
    } catch (e) {
      console.error(e);
      const serverMessage =
        (e.response && e.response.data && e.response.data.message) ||
        e.message ||
        "Transfer failed. Please try again.";
      toast.update(toastId, {
        render: serverMessage,
        type: "error",
        isLoading: false,
        autoClose: 8000,
        closeOnClick: true,
        draggable: true,
        closeButton: true,
      });
    } finally {
      setTransferring(false);
    }
  };

  /* ---------------- render ---------------- */
  return (
    <div className="container-fluid py-3">
      {/* Filters + buttons */}
      <div className="row g-3 align-items-end mb-3">
        <div className="col-12 col-md-3">
          <label className="form-label fw-semibold">Domain</label>
          <SelectInput
            options={domainOptions}
            value={
              domainOptions.find((o) => o.value === selectedDomain) || null
            }
            onChange={handleDomainChange}
            placeholder={
              isLoadingDomains ? "Loading domains…" : "Search domain…"
            }
            noOptionsMessage={() => "No domains found"}
            isDisabled={isLoadingDomains || busy}
            isLoading={isLoadingDomains}
          />
        </div>

        <div className="col-12 col-md-4">
          <label className="form-label fw-semibold">Current URL</label>
          <Select
            options={urlOptions}
            value={urlOptions.find((o) => o.value === selectedURL) || null}
            onChange={handleURLChange}
            placeholder={
              selectedDomain ? "Search URL…" : "Select a domain first"
            }
            isSearchable={!!selectedDomain}
            isClearable
            isDisabled={!selectedDomain || isLoadingUrls || busy}
            isLoading={isLoadingUrls}
            styles={selectStyles}
            classNamePrefix="react-select"
            noOptionsMessage={() => "No URLs found"}
          />
        </div>

        <div className="col-12 col-md-3">
          <label className="form-label fw-semibold" htmlFor={`${sourceId}-ids`}>
            Ref. IDs <span className="text-muted fw-normal">(optional)</span>
          </label>
          <input
            id={`${sourceId}-ids`}
            type="text"
            className="form-control"
            placeholder="e.g. 220, 221 or 220-222"
            value={idsText}
            onChange={(e) => setIdsText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && handleSearch()}
            disabled={busy}
            style={{ height: 38 }}
          />
        </div>

        <div className="col-12 col-md-2">
          <div className="d-flex gap-2">
            <button
              id={`${sourceId}-search`}
              className="btn btn-primary w-50 shadow-sm"
              onClick={handleSearch}
              disabled={busy}
              style={{ height: 38 }}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-label="Searching"
                />
              ) : (
                "Search"
              )}
            </button>
            <button
              id={`${sourceId}-transfer`}
              className="btn btn-success w-50 shadow-sm"
              onClick={Transfer}
              disabled={busy || selectedIds.length === 0}
              style={{ height: 38 }}
            >
              {transferring ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-label="Transferring"
                />
              ) : (
                "Transfer"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Selected count + Check all (right side, natural width) */}
      {filterData.length > 0 && (
        <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-2">
          <span className="text-muted">
            {selectedIds.length} of {filterData.length} selected
          </span>
          <button
            id={`${selectId}-checkAll`}
            className="btn btn-light border btn-md fs-5"
            onClick={checkAll}
            disabled={busy}
          >
            {selectAll ? "Uncheck all" : "Check all"}
          </button>

          {/* <UpdateTypeAction
            idPrefix={`${selectId}-type`}
            apiUrl={API_URL}
            domain={selectedDomain}
            selectedIds={selectedIds}
            disabled={loading || transferring}
            onUpdated={handleTypeUpdated}
            onBusyChange={setTypeUpdating}
          /> */}
        </div>
      )}

      {/* Table / empty states */}
      {loading ? (
        <div className="text-center py-5 text-muted border">
          <div className="spinner-border mb-3" role="status" />
          <div>Loading records…</div>
        </div>
      ) : currentPosts.length > 0 ? (
        <div className="table-responsive mb-2">
          <ContentFetchTable
            selectId={selectId}
            currentPosts={currentPosts}
            handleCheckboxChange={handleCheckboxChange}
            setFilterData={setFilterData}
            filterData={filterData}
            setData={setData}
            selectedIds={selectedIds}
            selectAll={selectAll}
            handleSelectAll={handleSelectAll}
          />
        </div>
      ) : (
        <div className="text-center py-5 text-muted border">
          <div className="fs-5 mb-1">
            {hasSearched ? "No records found" : "No data yet"}
          </div>
          <div className="small">
            {hasSearched
              ? "Try a different URL or clear the Ref. IDs filter."
              : "Pick a domain and URL, then select Search."}
          </div>
        </div>
      )}

      {filterData.length > 0 && (
        <div className="">
          <PaginationModule
            totalPages={howManyPages}
            postsPerPage={postsPerPage}
            setPostsPerPage={setPostsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default DataTransfer;